import React, { useState } from "react";
import {
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Card,
  Chip,
} from "@mui/material";
import { RiskItem } from "../types";
import { StatusChip } from "../../../shared/components/StatusChip";

interface RiskSectionProps {
  risks: RiskItem[];
  isLoading?: boolean;
  isError?: boolean;
  error?: string;
}

const getStatusChipColor = (
  status?: string
): "red" | "green" | "amber" | "default" => {
  if (!status) return "default";
  const lowerStatus = status.toLowerCase();
  if (lowerStatus.includes("mitigated") || lowerStatus.includes("closed"))
    return "green";
  if (lowerStatus.includes("progress") || lowerStatus.includes("active"))
    return "amber";
  if (lowerStatus.includes("risk") || lowerStatus.includes("open"))
    return "red";
  return "default";
};

const getRiskScoreColor = (score?: number): "red" | "green" | "amber" => {
  if (!score) return "amber";
  if (score >= 12) return "red";
  if (score >= 6) return "amber";
  return "green";
};

const RiskSection: React.FC<RiskSectionProps> = ({
  risks,
  isLoading = false,
  isError = false,
  error,
}) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 150,
          width: "100%",
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ width: "100%" }}>
        {error || "Failed to load risks"}
      </Alert>
    );
  }

  if (risks.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 100,
          width: "100%",
          color: "text.secondary",
        }}
      >
        <Typography variant="body2">No risks found</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {risks.map((risk) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }} key={risk.id}>
          <Card
            variant="outlined"
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                {risk.project_ref && (
                  <Typography variant="caption" color="text.secondary">
                    {risk.project_ref}
                  </Typography>
                )}
                {risk.status && (
                  <StatusChip
                    label={risk.status}
                    color={getStatusChipColor(risk.status)}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
              {typeof risk.risk_score === "number" && (
                <StatusChip
                  label={String(risk.risk_score)}
                  color={getRiskScoreColor(risk.risk_score)}
                  variant="filled"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            <Typography variant="subtitle2" color="text.primary" sx={{ mb: 0.5 }}>
              {risk.title}
            </Typography>
            {risk.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  flex: 1,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {risk.description}
              </Typography>
            )}
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

interface RisksAndIssuesProps {
  timeScheduleRisks: RiskItem[];
  costCommercialRisks: RiskItem[];
  safetyHseqRisks: RiskItem[];
  isLoadingTimeSchedule?: boolean;
  isLoadingCostCommercial?: boolean;
  isLoadingSafetyHseq?: boolean;
  isErrorTimeSchedule?: boolean;
  isErrorCostCommercial?: boolean;
  isErrorSafetyHseq?: boolean;
  errorTimeSchedule?: string;
  errorCostCommercial?: string;
  errorSafetyHseq?: string;
}

export const RisksAndIssues: React.FC<RisksAndIssuesProps> = ({
  timeScheduleRisks,
  costCommercialRisks,
  safetyHseqRisks,
  isLoadingTimeSchedule = false,
  isLoadingCostCommercial = false,
  isLoadingSafetyHseq = false,
  isErrorTimeSchedule = false,
  isErrorCostCommercial = false,
  isErrorSafetyHseq = false,
  errorTimeSchedule,
  errorCostCommercial,
  errorSafetyHseq,
}) => {
  const [selectedRiskCategory, setSelectedRiskCategory] = useState<'time' | 'cost' | 'quality'>('time');

  return (
    <Card variant="outlined" sx={{ p: 0 }}>

      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "action.hover",
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="subtitle2">Risk and Issues</Typography>
        <Typography variant="subtitle2">Top 5 risks by {selectedRiskCategory.charAt(0).toUpperCase() + selectedRiskCategory.slice(1)}</Typography>
      </Box>

      <Box p={2} display={'flex'} flexDirection={'column'} gap={2}>

        <Box display={'flex'} gap={2}>
          <Chip label="Time" variant={selectedRiskCategory === 'time' ? "filled" : "outlined"} onClick={() => setSelectedRiskCategory('time')} />
          <Chip label="Cost" variant={selectedRiskCategory === 'cost' ? "filled" : "outlined"} onClick={() => setSelectedRiskCategory('cost')} />
          <Chip label="Quality" variant={selectedRiskCategory === 'quality' ? "filled" : "outlined"} onClick={() => setSelectedRiskCategory('quality')} />
        </Box>

        {selectedRiskCategory === 'time' && (
          <RiskSection
            risks={timeScheduleRisks}
            isLoading={isLoadingTimeSchedule}
            isError={isErrorTimeSchedule}
            error={errorTimeSchedule}
          />
        )}
        {selectedRiskCategory === 'cost' && (
          <RiskSection
            risks={costCommercialRisks}
            isLoading={isLoadingCostCommercial}
            isError={isErrorCostCommercial}
            error={errorCostCommercial}
          />
        )}
        {selectedRiskCategory === 'quality' && (
          <RiskSection
            risks={safetyHseqRisks}
            isLoading={isLoadingSafetyHseq}
            isError={isErrorSafetyHseq}
            error={errorSafetyHseq}
          />
        )}

      </Box>

    </Card>
  );
};
